import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X, Clock3, Moon } from "lucide-react";
import { startTone, stopAllTones } from "@/lib/beeper";
import { startWakeLock, stopWakeLock } from "@/lib/wakelock";
import { closeFlashNotifications } from "@/lib/notifications";
import { useI18n } from "@/lib/i18n";

const SIZE_FACTOR = { sm: 0.6, md: 0.92, lg: 1.0 }; // horizontal fit factor
const SIZE_HEIGHT_FACTOR = { sm: 0.6, md: 0.86, lg: 0.95 };
const SCROLL_FONT_VW = { sm: "10vw", md: "15vw", lg: "22vw" };

const useFittedFontSize = (text, mode, textSize = "md") => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [fontSize, setFontSize] = useState(120);

  useLayoutEffect(() => {
    if (mode !== "fit") return;
    const container = containerRef.current;
    const measurer = measureRef.current;
    if (!container || !measurer) return;

    const fit = () => {
      const availW = container.clientWidth * (SIZE_FACTOR[textSize] || 0.92);
      const availH = container.clientHeight * (SIZE_HEIGHT_FACTOR[textSize] || 0.86);
      if (availW <= 0 || availH <= 0) return;

      let lo = 16;
      let hi = Math.floor(availH);
      let best = lo;
      for (let i = 0; i < 22; i++) {
        const mid = Math.floor((lo + hi) / 2);
        measurer.style.fontSize = `${mid}px`;
        measurer.style.lineHeight = "0.9";
        const w = measurer.scrollWidth;
        const h = measurer.scrollHeight;
        if (w <= availW && h <= availH) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
        if (lo > hi) break;
      }
      setFontSize(best);
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(container);
    window.addEventListener("orientationchange", fit);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", fit);
    };
  }, [text, mode, textSize]);

  return { containerRef, measureRef, fontSize };
};

const useCustomBgCycle = (colors) => {
  const [current, setCurrent] = useState(colors && colors[0]);
  useEffect(() => {
    if (!colors || colors.length === 0) return;
    setCurrent(colors[0]);
    if (colors.length < 2) return;
    let i = 0;
    const id = window.setInterval(() => {
      i = (i + 1) % colors.length;
      setCurrent(colors[i]);
    }, 500);
    return () => window.clearInterval(id);
  }, [colors]);
  return current;
};

export const FlashOverlay = ({
  message,
  alarmTone = "off",
  displayMode = "fit",
  textSize = "md",
  bgColors = null,
  quietActive = false,
  onStop,
  onSnooze,
  snoozeCount = 0,
  snoozeLimit = 3,
  autoStopMinutes = 15,
}) => {
  const { t } = useI18n();
  const stopRef = useRef(onStop);
  stopRef.current = onStop;
  const [snoozeMenuOpen, setSnoozeMenuOpen] = useState(false);
  const snoozeDisabled = snoozeCount >= snoozeLimit;

  const { containerRef, measureRef, fontSize } = useFittedFontSize(
    message,
    displayMode,
    textSize
  );
  const customBg = useCustomBgCycle(bgColors);
  const useCustomBg = Array.isArray(bgColors) && bgColors.length > 0;

  useEffect(() => {
    const el = document.documentElement;
    if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
    }

    if (alarmTone && alarmTone !== "off") startTone(alarmTone);
    startWakeLock();
    closeFlashNotifications();

    const mountedAt = Date.now();
    const graceMs = 400;
    const canStop = () => Date.now() - mountedAt >= graceMs;

    const handleKey = (e) => {
      if (!canStop()) return;
      e.preventDefault();
      stopRef.current?.();
    };
    const handleClick = () => {
      if (!canStop()) return;
      stopRef.current?.();
    };

    window.addEventListener("keydown", handleKey);
    window.addEventListener("click", handleClick);
    window.addEventListener("touchstart", handleClick);

    let autoStopTimer = null;
    if (autoStopMinutes && autoStopMinutes > 0) {
      autoStopTimer = window.setTimeout(() => {
        stopRef.current?.();
      }, autoStopMinutes * 60 * 1000);
    }

    return () => {
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("click", handleClick);
      window.removeEventListener("touchstart", handleClick);
      if (autoStopTimer) window.clearTimeout(autoStopTimer);
      stopAllTones();
      stopWakeLock();
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [alarmTone, autoStopMinutes]);

  return (
    <div
      data-testid="flash-overlay"
      ref={containerRef}
      style={useCustomBg ? { backgroundColor: customBg, transition: "background-color 180ms linear" } : undefined}
      className={`fixed inset-0 z-[9999] ${useCustomBg ? "" : "flash-bg"} flex items-center justify-center overflow-hidden cursor-pointer select-none`}
      role="alert"
      aria-live="assertive"
    >
      <div className="absolute top-6 right-6 z-10 flex gap-2">
        {quietActive && (
          <span
            data-testid="quiet-chip"
            className="bg-[#0014C7] text-white brutal-border px-3 py-3 font-mono text-xs uppercase tracking-widest flex items-center gap-1"
            title={t("quiet.hint")}
          >
            <Moon className="w-4 h-4" strokeWidth={3} />
            {t("quiet.activeChip")}
          </span>
        )}
        {onSnooze && (
          <div className="relative">
            <button
              data-testid="flash-snooze-btn"
              disabled={snoozeDisabled}
              onClick={(e) => {
                e.stopPropagation();
                if (snoozeDisabled) return;
                setSnoozeMenuOpen((v) => !v);
              }}
              className={`brutal-border brutal-shadow brutal-interactive px-4 py-3 font-display uppercase text-base flex items-center gap-2 ${
                snoozeDisabled
                  ? "bg-neutral-300 text-neutral-500 cursor-not-allowed"
                  : "bg-[#E5FF00] text-black"
              }`}
            >
              <Clock3 className="w-5 h-5" strokeWidth={3} />
              {snoozeDisabled ? "No snooze left" : `Snooze (${snoozeLimit - snoozeCount} left)`}
            </button>
            {snoozeMenuOpen && !snoozeDisabled && (
              <div
                className="absolute right-0 top-full mt-2 bg-white brutal-border brutal-shadow p-2 flex flex-col gap-2 min-w-[160px]"
                onClick={(e) => e.stopPropagation()}
              >
                {[1, 5, 10, 30].map((m) => (
                  <button
                    key={m}
                    data-testid={`snooze-${m}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSnoozeMenuOpen(false);
                      onSnooze?.(m);
                    }}
                    className="bg-white text-black brutal-border font-display uppercase text-sm px-3 py-2 brutal-shadow-sm brutal-interactive"
                  >
                    {m}m
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <button
          data-testid="flash-stop-btn"
          onClick={(e) => {
            e.stopPropagation();
            onStop?.();
          }}
          className="bg-white text-black brutal-border brutal-shadow brutal-interactive px-5 py-3 font-display uppercase text-lg flex items-center gap-2"
        >
          <X className="w-5 h-5" strokeWidth={3} /> Stop
        </button>
      </div>

      {displayMode === "scroll" ? (
        <div className="w-full overflow-hidden">
          <div
            data-testid="flash-message"
            className="marquee font-display text-white flash-text leading-none"
            style={{ fontSize: SCROLL_FONT_VW[textSize] || "15vw" }}
          >
            {message}
          </div>
        </div>
      ) : (
        <>
          <span
            ref={measureRef}
            aria-hidden="true"
            className="font-display absolute invisible whitespace-nowrap pointer-events-none"
            style={{ left: -99999, top: -99999, lineHeight: 0.9 }}
          >
            {message}
          </span>
          <div
            data-testid="flash-message"
            className="font-display text-white flash-text text-center whitespace-nowrap px-4"
            style={{ fontSize: `${fontSize}px`, lineHeight: 0.9 }}
          >
            {message}
          </div>
        </>
      )}

      <div className="absolute bottom-6 left-0 right-0 text-center pointer-events-none">
        <span className="inline-block bg-black text-white font-mono text-xs md:text-sm uppercase tracking-widest px-3 py-2 brutal-border border-white">
          Press any key · click · or Stop to end
        </span>
      </div>
    </div>
  );
};

export default FlashOverlay;
